import React, { useCallback, useEffect, useRef, useState } from "react";
import { Row, Col, Button, message, Form, Input, Checkbox, Tooltip } from 'antd';

import apiClient from 'services/apiClient';
import Flex from "components/shared-components/Flex";
import { ArrowUpOutlined, ArrowDownOutlined, WalletOutlined, InfoCircleOutlined, ReloadOutlined, InfoCircleFilled, CalendarOutlined, PlusOutlined, MinusOutlined, FileTextOutlined, HistoryOutlined } from '@ant-design/icons';
import { CustomCard, CustomBadge, CustomAlertBanner } from '../../components/AlertBadge';

import moment from "moment";

import BillingPricingModel from "./billin_pricing_model";
import TenantSellRatesTable from "./tenant_sell_rates_table";
import { useMyContext } from "Context/MyContextProvider";
import PermissionChecker from "../../Utils/PermissionChecker";
import { ROW_GUTTER } from "constants/ThemeConstant";
import { SUCCESS, DANGER } from 'views/app-views/whatsapp/CustomUtils.js/utils';


export const DefaultDashboard = ({ id }) => {


  const { UserCredits, api, authToken, themeData, broadcastBalanceUpdate, Permisson } = useMyContext();
  const canEditPricing = Permisson?.includes('Add or Update Pricing Model');

  const [initialAmount, setInitialAmount] = useState('00:00');
  const [amount, setAmount] = useState('00:00')
  const [newCredit, setNewCredit] = useState()
  const [remarks, setRemarks] = useState()
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [deduction, setDeduction] = useState(false)
  const inputRef = useRef(null);
  const [inputVal, settInputVal] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [timeframe, setTimeframe] = useState('all');

  const [form] = Form.useForm();


  const UpdateBalance = async (e) => {
    if (newCredit) {
      await apiClient.post(`${api}add-balance`, {
        amount, user_id: id, newCredit, deduction, remarks
      }, {
        headers: {
          'Authorization': 'Bearer ' + authToken,
        }
      })
        .then((response) => {
          if (response.data.status) {
            fetchHistory();
            UserCredits();

            // Broadcast balance update to all tabs
            broadcastBalanceUpdate(amount);

            message.success(response.data.message);
            if (inputRef.current) {
              inputRef.current.value = '';
            }
            form.setFieldsValue({ Amount: undefined, remarks: undefined });
            setNewCredit(undefined);
            settInputVal('');
            setRemarks('');
          }
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }


  const fetchHistory = useCallback(async () => {
    setLoading(true)
    await apiClient.get(`${api}balance-history/${id}`, {
      headers: {
        'Authorization': 'Bearer ' + authToken,
      }
    })
      .then((response) => {
        let data = response.data
        const filteredHistory = data?.history;
        setHistory(filteredHistory);
        setAmount(data?.latest_balance || 0);
        setInitialAmount(data?.latest_balance || 0)
      })
      .catch((error) => {
        // console.log(error);
      }).finally(() => setLoading(false));
  }, [api, id, authToken]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory])


  const handleAmount = useCallback((value) => {
    setNewCredit(value);
    settInputVal(value)
    if (value && !isNaN(value)) {
      if (deduction) {
        if (value > initialAmount) {
          setAmount('00.00')
        } else {
          setAmount(Math.abs(parseFloat(value) - parseFloat(initialAmount)));
        }
      } else {
        setAmount(parseFloat(value) + parseFloat(initialAmount));
      }

    } else {
      setAmount(initialAmount);
    }
  }, [deduction, initialAmount]);



  useEffect(() => {
    if (amount) {
      const parts = amount.toString().split('.');
      const decimalDigits = parts.length > 1 ? parts[1].slice(0, 2) : '';
      const formattedAmount = parts[0] + (decimalDigits ? '.' + decimalDigits : '');
      setAmount(formattedAmount);
    }
  }, [amount])

  useEffect(() => {
    handleAmount(newCredit);
  }, [deduction, handleAmount, newCredit])

  const getFilteredHistory = () => {
    if (!history) return [];
    const reversedHistory = [...history].reverse();
    if (timeframe === 'today') {
      return reversedHistory.filter(item => moment(item.created_at).isSame(moment(), 'day'));
    } else if (timeframe === 'week') {
      return reversedHistory.filter(item => moment(item.created_at).isAfter(moment().subtract(7, 'days')));
    } else if (timeframe === 'month') {
      return reversedHistory.filter(item => moment(item.created_at).isAfter(moment().subtract(30, 'days')));
    }
    return reversedHistory;
  };

  const filteredHistory = getFilteredHistory();
  const paginatedHistory = filteredHistory.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <>

      <Row gutter={ROW_GUTTER}>
        <Col xs={24} md={14}>
          {(Permisson?.includes('View Billing') || Permisson?.includes('View Pricing Model')) && (
            canEditPricing ? (
              <BillingPricingModel id={id} api={api} authToken={authToken} />
            ) : (
              <TenantSellRatesTable
                id={id}
                api={api}
                authToken={authToken}
                balance={amount}
                balanceLoading={loading}
              />
            )
          )}
        </Col>

        <Col xs={24} md={10}>
          <Row gutter={ROW_GUTTER}>
            <PermissionChecker permission="View Available Credit">
              {canEditPricing && (
                <Col span={24} className="mb-3">
                  <CustomCard style={{ background: themeData.currentTheme === 'dark' ? '#1b2531' : '#ffffff' }} bodyStyle={{ padding: '24px' }}>
                    <Flex justifyContent="space-between" alignItems="center">
                      <Flex alignItems="center" gap="16px">
                        <div className="manage-alerts-header-icon-wrapper" style={{ background: '#e8f5e9', borderColor: '#c8e6c9', color: '#2e7d32' }}>
                          <WalletOutlined />
                        </div>
                        <div className="manage-alerts-header-text">
                          <h4 className="manage-alerts-header-title" style={{ fontSize: '16px', fontWeight: 'bold' }}>Available Credit</h4>
                          <p className="manage-alerts-header-desc my-0" style={{ fontSize: '12px', color: '#72849a' }}>Current wallet balance</p>
                        </div>
                      </Flex>
                      <PermissionChecker permission="Deduct Credit">
                        <Tooltip title="Check this box if you want to deduct credit">
                          <Checkbox checked={deduction} onChange={(e) => setDeduction(e.target.checked)} style={{ fontSize: '13px', fontWeight: '500' }}>
                            Deduction <InfoCircleOutlined style={{ fontSize: '12px', marginLeft: '2px', color: '#8c8c8c' }} />
                          </Checkbox>
                        </Tooltip>
                      </PermissionChecker>
                    </Flex>

                    <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '13px', color: '#72849a', fontWeight: '500', display: 'block', marginBottom: '4px' }}>Available Credits</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h1 style={{ fontSize: '36px', fontWeight: '800', margin: 0, color: '#1a3353' }}>
                            {!loading ? '₹' + amount : 'fetching...'}
                          </h1>
                          <CustomBadge type="success" pill>
                            <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', background: SUCCESS, marginRight: '4px' }}></span>
                            Active
                          </CustomBadge>
                        </div>
                      </div>
                    </div>

                    <hr style={{ border: 'none', borderTop: '1px solid #f0f0f0', margin: '20px 0' }} />

                    <PermissionChecker permission='Add Credit'>
                      <div className="balance-form">
                        <Form
                          form={form}
                          name="credit"
                          layout="vertical"
                          onFinish={(values) => UpdateBalance(values)}
                        >
                          <Form.Item
                            label={<span style={{ fontWeight: '600' }}>{`${deduction ? 'Deduct' : 'Add'} Credit`} <span style={{ color: '#ff4d4f' }}>*</span></span>}
                            name="Amount"
                            rules={[
                              {
                                required: true,
                                message: 'Please enter your amount!'
                              },
                              {
                                validator: (_, value) => {
                                  if (value && parseFloat(value) > 10000000) {
                                    return Promise.reject(new Error('Amount cannot exceed 1 Crore (₹1,00,00,000)'));
                                  }
                                  return Promise.resolve();
                                }
                              }
                            ]}
                            initialValue={inputVal}
                            extra={'Enter the amount you want to ' + (deduction ? 'deduct from' : 'add to') + ' the wallet.'}
                          >
                            <Input
                              size="large"
                              placeholder="Enter amount"
                              prefix={<span style={{ color: '#595959', fontWeight: '500', marginRight: '4px' }}>₹</span>}
                              style={{ borderRadius: '8px' }}
                              ref={inputRef}
                              name="amount"
                              onKeyDown={(event) => {
                                const { key, target } = event;
                                if (key === 'Backspace' || key === 'ArrowLeft' || key === 'ArrowRight') {
                                  return;
                                }
                                if (!(/^\d$/.test(event.key) || event.key === '.')) {
                                  event.preventDefault();
                                } else if (target.value.includes('.')) {
                                  const decimalIndex = target.value.indexOf('.');
                                  const decimalPart = target.value.substring(decimalIndex + 1);
                                  if (decimalPart.length >= 2) {
                                    event.preventDefault();
                                  }
                                } else {
                                  return
                                }
                              }}
                              onChange={(e) => handleAmount(parseFloat(e.target.value))}
                            />
                          </Form.Item>

                          <Form.Item
                            name="remarks"
                            label={
                              <span style={{ fontWeight: '600' }}>
                                Remarks
                              </span>
                            }
                            extra={'Add any notes or remarks for this transaction.'}
                          >
                            <Input.TextArea
                              rows={2}
                              placeholder="Enter remarks (optional)"
                              maxLength={200}
                              showCount={{
                                formatter: ({ count, maxLength }) => `${count}/${maxLength}`
                              }}
                              style={{ borderRadius: '8px' }}
                              autoComplete="off"
                              onChange={e => setRemarks(e.target.value)}
                            />
                          </Form.Item>

                          <div style={{ marginBottom: '20px' }}>
                            <CustomAlertBanner
                              icon={<InfoCircleFilled />}
                              title={<span style={{ fontSize: '13px' }}>Credit will be {deduction ? 'deducted from' : 'added to'} your wallet instantly.</span>}
                              subtitle={<span style={{ fontSize: '13px' }}>Updated balance is available in Payment History.</span>}
                            />
                          </div>

                          <Flex justifyContent="space-between" alignItems="center">
                            <Button
                              style={{ borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}
                              onClick={() => {
                                form.setFieldsValue({ Amount: undefined, remarks: undefined });
                                if (inputRef.current) {
                                  inputRef.current.value = '';
                                }
                                setRemarks('');
                                setNewCredit(undefined);
                                settInputVal('');
                                setDeduction(false);
                                setAmount(initialAmount);
                              }}
                            >
                              <ReloadOutlined /> Reset
                            </Button>
                            <Button
                              type="primary"
                              htmlType="submit"
                            >
                              <WalletOutlined /> Submit
                            </Button>
                          </Flex>
                        </Form>
                      </div>
                    </PermissionChecker>
                  </CustomCard>
                </Col>
              )}
            </PermissionChecker>
            <PermissionChecker permission="View Payment History">
              <Col span={24}>
                <CustomCard
                  style={{
                    background: themeData.currentTheme === 'dark' ? '#1b2531' : '#ffffff',
                    minHeight: canEditPricing ? undefined : 280,
                    display: canEditPricing ? undefined : 'flex',
                    flexDirection: canEditPricing ? undefined : 'column',
                  }}
                >
                  <Flex justifyContent="space-between" alignItems="center" style={{ marginBottom: '24px' }}>
                    <Flex alignItems="center" gap="16px">
                      <div className="manage-alerts-header-icon-wrapper" style={{ background: '#e6f7ff', borderColor: '#91d5ff', color: '#1890ff' }}>
                        <FileTextOutlined />
                      </div>
                      <div className="manage-alerts-header-text">
                        <h4 className="manage-alerts-header-title" style={{ fontSize: '16px', fontWeight: 'bold' }}>Payment History</h4>
                        <p className="manage-alerts-header-desc my-0" style={{ fontSize: '12px', color: '#72849a' }}>View all your credit additions and deductions.</p>
                      </div>
                    </Flex>
                  </Flex>

                  <div
                    className="mt-3"
                    style={{
                      flex: canEditPricing ? undefined : 1,
                      maxHeight: canEditPricing ? '480px' : undefined,
                      overflowY: 'auto',
                    }}
                  >
                    {filteredHistory?.length > 0 ? (
                      paginatedHistory.map((item, index) => {
                        const isDeduction = item?.manual_deduction;
                        return (
                          <Tooltip title={item?.remarks ? 'Remarks: ' + item?.remarks : 'No remarks'} key={index}>
                            <div
                              style={{
                                border: '1px solid #f0f0f0',
                                borderRadius: '10px',
                                padding: '10px 14px',
                                marginBottom: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                transition: 'all 0.3s',
                                background: themeData.currentTheme === 'dark' ? '#172230' : '#ffffff'
                              }}
                              className="payment-history-item"
                            >
                              <Flex alignItems="center" gap="12px">
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  background: isDeduction ? 'rgba(255, 77, 79, 0.1)' : 'rgba(82, 196, 26, 0.1)',
                                  color: isDeduction ? DANGER : SUCCESS,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}>
                                  {isDeduction ? <MinusOutlined style={{ fontSize: '12px' }} /> : <PlusOutlined style={{ fontSize: '12px' }} />}
                                </div>
                                <Flex flexDirection="column">
                                  <h5 style={{ fontSize: '14px', fontWeight: '600', margin: 0, color: themeData.currentTheme === 'dark' ? '#ffffff' : '#1a3353' }}>
                                    ₹{parseFloat(item?.total_credits).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </h5>
                                  <span style={{ fontSize: '11px', color: '#72849a', marginTop: '2px', display: 'block' }}>
                                    {moment(item.created_at).format('MMMM Do YYYY, h:mm:ss a')}
                                  </span>
                                </Flex>
                              </Flex>

                              <div style={{
                                borderLeft: '1px solid #f0f0f0',
                                paddingLeft: '16px',
                                textAlign: 'right',
                                minWidth: '100px'
                              }}>
                                {/* {isDeduction ? (
                                  <CustomBadge style={{ background: '#fff1f0', borderColor: '#ffa39e', color: '#ff4d4f', fontSize: '10px', padding: '1px 6px' }} pill>
                                    Credit Deducted
                                  </CustomBadge>
                                ) : (
                                  <CustomBadge type="success" style={{ fontSize: '10px', padding: '1px 6px' }} pill>
                                    Credit Added
                                  </CustomBadge>
                                )} */}
                                <div style={{
                                  fontSize: '13px',
                                  fontWeight: '600',
                                  color: isDeduction ? DANGER : SUCCESS,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'flex-end',
                                  gap: '2px',
                                  marginTop: '4px'
                                }}>
                                  <span>
                                    {isDeduction ? '-' : '+'}
                                    {parseFloat(item.new_credit).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                  {isDeduction ? <ArrowDownOutlined style={{ fontSize: '11px' }} /> : <ArrowUpOutlined style={{ fontSize: '11px' }} />}
                                </div>
                              </div>
                            </div>
                          </Tooltip>
                        );
                      })
                    ) : (
                      <div style={{ textAlign: 'center', padding: '24px 0' }}>
                        <span style={{ fontSize: '13px', color: '#72849a' }}>There is no payment history for you</span>
                      </div>
                    )}
                  </div>

                </CustomCard>
              </Col>
            </PermissionChecker>
          </Row>
        </Col>

      </Row>


    </>
  )
}


export default DefaultDashboard;
